/**
 * Frozen declarative contract for Waiting Pool Entry v1.
 *
 * This module contains identifiers, value sets, exact acknowledgement copy,
 * limits, and policy constants only. Importing it must not perform I/O or
 * change Worker, API, UI, database, or deployment behavior.
 */

export const WAITING_POOL_ENTRY_ID = "relationship-waiting-pool-entry" as const;
export const WAITING_POOL_ENTRY_VERSION = "1.0.1" as const;
export const WAITING_POOL_ENTRY_VERSION_ID =
  `${WAITING_POOL_ENTRY_ID}@${WAITING_POOL_ENTRY_VERSION}` as const;

export const CANDIDATE_VISIBLE_PROJECTION_SCHEMA_VERSION_ID =
  "candidate-visible-projection@1.0.0" as const;

export const WAITING_POOL_ENTRY_IMPLEMENTATION_PLAN_ID =
  "relationship-waiting-pool-entry-implementation-plan" as const;
export const WAITING_POOL_ENTRY_IMPLEMENTATION_PLAN_VERSION = "1.0.2" as const;
export const WAITING_POOL_ENTRY_IMPLEMENTATION_PLAN_VERSION_ID =
  `${WAITING_POOL_ENTRY_IMPLEMENTATION_PLAN_ID}@${WAITING_POOL_ENTRY_IMPLEMENTATION_PLAN_VERSION}` as const;

export const WAITING_POOL_MATCHING_MODE = "romantic" as const;

export const PARTICIPATION_STATUSES = [
  "not_entered",
  "active",
  "paused",
  "ended",
] as const;
export type ParticipationStatus = (typeof PARTICIPATION_STATUSES)[number];

export const ACTIVE_ELIGIBILITY_STATES = ["eligible", "action_required"] as const;
export type ActiveEligibilityState = (typeof ACTIVE_ELIGIBILITY_STATES)[number];

export const PARTICIPATION_UI_STATES = [
  "not_entered",
  "paused",
  "ended",
  "action_required",
  "waiting",
] as const;
export type ParticipationUiState = (typeof PARTICIPATION_UI_STATES)[number];

export const PARTICIPATION_TRANSITIONS = [
  ["not_entered", "active"],
  ["active", "paused"],
  ["paused", "active"],
  ["paused", "ended"],
] as const satisfies readonly (readonly [ParticipationStatus, ParticipationStatus])[];

export const CURRENT_RELATIONSHIP_GOALS = [
  "open_to_connection",
  "dating",
  "stable_partner",
  "long_term",
] as const;
export type CurrentRelationshipGoal = (typeof CURRENT_RELATIONSHIP_GOALS)[number];

export const GENDER_MATCH_CATEGORIES = [
  "woman",
  "man",
  "nonbinary_or_gender_diverse",
] as const;
export type GenderMatchCategory = (typeof GENDER_MATCH_CATEGORIES)[number];

export const AGE_BANDS = [
  "18_24",
  "25_29",
  "30_34",
  "35_39",
  "40_49",
  "50_59",
  "60_plus",
] as const;
export type AgeBand = (typeof AGE_BANDS)[number];

export const AGE_BAND_CONFIRMATION_MAX_MONTHS = 12 as const;

export const CANDIDATE_VISIBLE_TEXT_LIMITS = {
  display_name: 40,
  custom_gender_display_label: 80,
  pronouns: 80,
  short_introduction: 600,
  before_you_meet_me_text: 1000,
} as const;
export type CandidateVisibleTextField = keyof typeof CANDIDATE_VISIBLE_TEXT_LIMITS;

export const CANDIDATE_VISIBLE_PROFILE_TIMESTAMP_POLICY = {
  publishedAtMeaning:
    "server_time_current_published_revision_became_current_candidate_visible_profile",
  updatedAtMeaning:
    "server_time_current_published_row_last_persisted_modification",
  firstPublication: {
    publishedRevision: 1,
    publishedAt: "server_now",
    updatedAt: "same_server_now",
  },
  publishedProfileReplacement: {
    publishedRevision: "previous_revision_plus_one",
    publishedAt: "server_now",
    updatedAt: "same_server_now",
    preservePreviousPublishedAt: false,
  },
  publishedSourceResume: {
    ageBandReconfirmation: "required",
    ageBandMustEqualCurrentPublishedBand: true,
    publishedRevision: "preserve",
    publishedAt: "preserve",
    ageBandConfirmedAt: "server_now",
    updatedAt: "same_server_now",
    profileProse: "preserve",
  },
  unchangedAgeBandReconfirmation: {
    publishedRevision: "unchanged",
    publishedAt: "unchanged",
    ageBandConfirmedAt: "server_now",
    updatedAt: "same_server_now",
  },
  excludedMutations: [
    "draft_create_or_edit",
    "matching_preference_mutation",
    "acknowledgement",
    "pause",
    "ordinary_preview",
  ],
  failedConflictingOrRolledBackActionsChangeTimestamps: false,
  idempotentReplayPublishedAt: "first_success_persisted_value",
  getResponsePublishedRevisionAndPublishedAtDescribeCurrentVersion: true,
  updatedAtMayBeLaterThanPublishedAt: true,
  firstHistoricalPublicationTimestamp: "not_stored_or_exposed",
} as const;
export type CandidateVisibleProfileTimestampPolicy =
  typeof CANDIDATE_VISIBLE_PROFILE_TIMESTAMP_POLICY;

export const PROFILE_SOURCES = ["draft", "published"] as const;
export type ProfileSource = (typeof PROFILE_SOURCES)[number];

export const MATCHING_PREFERENCE_RECORD_STATES = [
  "absent",
  "incomplete",
  "complete",
] as const;
export type MatchingPreferenceRecordState =
  (typeof MATCHING_PREFERENCE_RECORD_STATES)[number];

export const PARTICIPATION_ACKNOWLEDGEMENT_TYPES = [
  "adult_declaration",
  "matching_pool_acknowledgement",
] as const;
export type ParticipationAcknowledgementType =
  (typeof PARTICIPATION_ACKNOWLEDGEMENT_TYPES)[number];

export const ADULT_DECLARATION_VERSION_ID = "adult-declaration@1.0.0" as const;
export const MATCHING_POOL_ACKNOWLEDGEMENT_VERSION_ID =
  "matching-pool-acknowledgement@1.3.0" as const;

export interface FrozenAcknowledgementCopy {
  readonly type: ParticipationAcknowledgementType;
  readonly versionId: string;
  readonly requiresReacknowledgement: boolean;
  readonly zh: {
    readonly title: string;
    readonly body: string;
    readonly confirmation: string;
  };
  readonly en: {
    readonly title: string;
    readonly body: string;
    readonly confirmation: string;
  };
}

export const PARTICIPATION_ACKNOWLEDGEMENTS = {
  adult_declaration: {
    type: "adult_declaration",
    versionId: ADULT_DECLARATION_VERSION_ID,
    requiresReacknowledgement: true,
    zh: {
      title: "成年与年龄段确认",
      body: "Perhaps 仅向年满 18 周岁的人开放。Perhaps 不要求你提供出生日期或精确年龄，但需要你选择并定期确认当前年龄段。若连续 12 个月没有确认，在完成确认前，你将暂时不会被纳入新的匹配，也不会收到新的认识邀请。这不会结束你的参与，也不会删除你的账户或资料。",
      confirmation: "我确认自己已年满 18 周岁，并确认所选年龄段目前准确。",
    },
    en: {
      title: "Adult and age-band declaration",
      body: "Perhaps is available only to people aged 18 or older. It does not ask for your date of birth or exact age, but it does require you to select and periodically reconfirm your current age band. If you go 12 consecutive months without confirming it, you will be temporarily excluded from new matching and will not receive new invitations to connect until you confirm it again. This will not end your participation or delete your account or information.",
      confirmation: "I confirm that I am at least 18 years old and that my selected age band is currently accurate.",
    },
  },
  matching_pool_acknowledgement: {
    type: "matching_pool_acknowledgement",
    versionId: MATCHING_POOL_ACKNOWLEDGEMENT_VERSION_ID,
    requiresReacknowledgement: true,
    zh: {
      title: "加入 Matching Pool",
      body: "加入匹配后，你确认展示的资料不会公开在互联网上，也不会向 Matching Pool 中的所有人展示。Perhaps 只会在你主动选择参与的关系类型中使用这些资料。原始问卷回答、私密偏好和内部评估不会随资料展示给对方。\n\n当前试点仅开放浪漫关系匹配。双方当前的参与条件和匹配范围都允许时，系统会按既定规则自动评估、推荐人选，并在相关参与者的账户中展示已确认的资料。你看到某个人，不代表对方已经看到了你或表示愿意认识你，也不代表 Perhaps 认定你们合适。你们会分别、独立地决定是否愿意进一步认识。\n\n未来如果 Perhaps 开放友谊或其他关系类型，你不会因为已经加入 Matching Pool 而自动参与这些新的关系类型。是否参与，需要由你另外作出明确选择。\n\n如果资料可见范围、人选推荐与展示规则，或你所参与关系类型的使用方式发生实质变化，Perhaps 会要求你重新确认。在你确认之前，不会将旧确认视为对新规则的同意。\n\n加入 Matching Pool 不代表马上就有人选。没有真实且达到当前展示条件的人选时，你会看到等待状态；系统不会创建虚假候选，也不会为了填满结果而降低硬性条件或质量门槛。\n\n你可以修改当前资料和匹配偏好，但已提交的问卷及当时的填写背景不会被改写。暂停参与、永久结束匹配参与和删除账户是三个不同的操作。",
      confirmation: "我已预览将展示给对方的资料，并选择加入当前的浪漫关系匹配。我理解系统会在参与条件和匹配范围允许时，按既定规则自动推荐人选，并向相关参与者展示这些资料；人选展示可能是单向的，不代表双方已表示愿意认识，也不代表 Perhaps 认定我们合适。我会独立决定是否进一步认识。未来新增关系类型需要由我另外明确选择。我理解可能需要等待，以及暂停参与、永久结束匹配参与和删除账户之间的区别。"
    },
    en: {
      title: "Joining the Matching Pool",
      body: "After you join matching, the profile you have confirmed for sharing will not be published on the public internet or shown to everyone in the pool. Perhaps will use this profile only within relationship modes that you have actively chosen to participate in. Your raw questionnaire answers, private preferences, and internal assessments will not be shown to the other person with your profile.\n\nThe current pilot supports Romantic matching only. When both people meet the current participation requirements and each person's matching boundaries allow it, the system automatically evaluates and recommends candidates under the established rules, and shows confirmed profiles in the relevant participants' accounts. Seeing someone does not mean they have already seen you or said they want to get to know you. It also does not mean Perhaps has decided that you are compatible. Each of you independently decides whether you want to get to know the other person.\n\nIf Perhaps later introduces Friendship or other relationship modes, joining the Matching Pool does not automatically enroll you in those modes. You will need to make a separate, explicit choice to participate.\n\nIf profile visibility, candidate recommendation and display rules, or the way your selected relationship modes are used materially change, Perhaps will ask you to reconfirm. Until you do, your earlier confirmation will not count as agreement to the new rules.\n\nJoining the Matching Pool does not guarantee an immediate candidate. If there is no real person who currently meets the display requirements, you will see a waiting state. The system will not create fake candidates or lower hard requirements or quality thresholds just to fill results.\n\nYou may update your current profile and matching preferences, but your submitted questionnaire and the context in which you completed it will not be rewritten. Pausing participation, permanently ending matching participation, and deleting your account are three separate actions.",
      confirmation: "I have previewed the profile that may be shown to another person and choose to join the current Romantic matching mode. I understand that, when participation requirements and matching boundaries allow it, the system automatically recommends candidates under the established rules and shows these profiles to the relevant participants. A profile may be shown in only one direction; this does not mean both people have expressed interest or that Perhaps has decided we are compatible. I will independently decide whether I want to get to know the other person. I will need to choose any future relationship modes separately and explicitly. I understand that I may need to wait, and that pausing participation, permanently ending matching participation, and deleting my account are different actions."
    }
  },
} as const satisfies Record<ParticipationAcknowledgementType, FrozenAcknowledgementCopy>;

export const PREVIEW_LOCALE_IDS = ["zh", "en"] as const;
export type PreviewLocaleId = (typeof PREVIEW_LOCALE_IDS)[number];

export const PREVIEW_LOCALE_CANONICALIZATION_POLICY = {
  explicitLocalePrecedesAcceptLanguage: true,
  trimAsciiWhitespace: true,
  lowercaseBeforeMatching: true,
  normalizeUnderscoreToHyphen: true,
  primarySubtagMap: { zh: "zh", en: "en" },
  acceptLanguageSelection: "highest_quality_then_header_order",
  missingOrBlankFallback: "zh",
  unsupportedNonEmptyFallback: "en",
  fingerprintUsesCanonicalLocaleOnly: true,
} as const;

export const CANDIDATE_VISIBLE_LOCATION_DISPLAY_FALLBACK = [
  "request_locale_preferred",
  "english_preferred",
  "canonical_name",
  "ascii_name",
] as const;

export const PARTICIPANT_ACTION_REQUEST_STATUSES = ["claimed", "completed"] as const;
export type ParticipantActionRequestStatus =
  (typeof PARTICIPANT_ACTION_REQUEST_STATUSES)[number];

export const PARTICIPANT_ACTION_TYPES = [
  "profile.publish",
  "profile.age_band.confirm",
  "profile.delete_ended",
  "participation.enter",
  "participation.pause",
  "participation.resume",
  "participation.end",
  "participation.active_save_and_pause",
  "location.active_transition",
] as const;
export type ParticipantActionType = (typeof PARTICIPANT_ACTION_TYPES)[number];

export const PARTICIPANT_ACTION_REQUEST_RETENTION_V1 = {
  ttlDeletion: false,
  scheduledCleanup: false,
  keyReuseAfterExpiry: false,
  retainedForAccountLifetime: true,
  removedByAccountDeletionCascade: true,
} as const;

export const WAITING_POOL_ENTRY_ROLLOUT_MODES = [
  "closed_verification",
  "open",
] as const;
export type WaitingPoolEntryRolloutMode =
  (typeof WAITING_POOL_ENTRY_ROLLOUT_MODES)[number];

export const PRODUCTION_CONTRACT_MIGRATION_POLICY = {
  expandMigration: "0007",
  contractMigration: "0008",
  preContractMode: "closed_verification",
  activeParticipantCountRequiredBeforeContract: 0,
  contractMigrationRequiresSeparateAuthorization: true,
  openingRequiresPostMigrationVerification: true,
  openingRequiresSeparateAuthorization: true,
  pinnedWranglerVersion: "4.114.0",
  contractMigrationRequiredTransactionalBatchCount: 1,
} as const;

export const ACTION_REQUIRED_REASON_CODES = [
  "age_band_confirmation_stale",
  "adult_declaration_reacknowledgement_required",
  "matching_pool_reacknowledgement_required",
  "location_confirmation_required",
  "active_location_boundary_invalid",
  "active_preferences_invalid_after_policy_change",
  "published_profile_unavailable",
] as const;
export type ActionRequiredReasonCode = (typeof ACTION_REQUIRED_REASON_CODES)[number];

export const WAITING_POOL_ENTRY_NON_GOALS = [
  "pair_score_generation",
  "candidate_query_or_results",
  "synthetic_candidates",
  "connection_intents",
  "contact_unlock",
  "friendship_lens",
  "cross_account_profile_reads",
] as const;

export const WAITING_POOL_ENTRY_V1 = {
  id: WAITING_POOL_ENTRY_ID,
  version: WAITING_POOL_ENTRY_VERSION,
  versionId: WAITING_POOL_ENTRY_VERSION_ID,
  candidateVisibleProjectionSchemaVersionId:
    CANDIDATE_VISIBLE_PROJECTION_SCHEMA_VERSION_ID,
  implementationPlanVersionId: WAITING_POOL_ENTRY_IMPLEMENTATION_PLAN_VERSION_ID,
  matchingMode: WAITING_POOL_MATCHING_MODE,
  participationStatuses: PARTICIPATION_STATUSES,
  activeEligibilityStates: ACTIVE_ELIGIBILITY_STATES,
  relationshipGoals: CURRENT_RELATIONSHIP_GOALS,
  genderMatchCategories: GENDER_MATCH_CATEGORIES,
  ageBands: AGE_BANDS,
  ageBandConfirmationMaxMonths: AGE_BAND_CONFIRMATION_MAX_MONTHS,
  textLimits: CANDIDATE_VISIBLE_TEXT_LIMITS,
  candidateVisibleProfileTimestampPolicy:
    CANDIDATE_VISIBLE_PROFILE_TIMESTAMP_POLICY,
  acknowledgementVersions: [
    ADULT_DECLARATION_VERSION_ID,
    MATCHING_POOL_ACKNOWLEDGEMENT_VERSION_ID,
  ],
  previewLocaleIds: PREVIEW_LOCALE_IDS,
  nonGoals: WAITING_POOL_ENTRY_NON_GOALS,
} as const;
